import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAI } from '../../hooks/useAI'
import { useOwner } from '../../contexts/OwnerContext'
import { AIAssistantModal } from '../modals/AIAssistantModal'

interface AIAssistantButtonProps {
  screenContext?: string
}

export const AIAssistantButton: React.FC<AIAssistantButtonProps> = ({
  screenContext,
}) => {
  const navigate = useNavigate()
  const { headers } = useOwner()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { messages, isLoading, error, askQuestion, clearMessages, clearError } =
    useAI({
      onSuccess: () => {
        // Opcional: fazer algo quando a resposta chegar
      },
      onError: (errorMsg) => {
        console.error('Erro na IA:', errorMsg)
      },
    })

  const handleOpenModal = () => {
    setIsModalOpen(true)
    clearError()
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    clearMessages()
    clearError()
  }

  const handleSendMessage = (message: string, context?: string) => {
    askQuestion(message, context || screenContext)
  }

  const handleOpenSpecializedQuestions = () => {
    // Navegar para a tela de perguntas especializadas passando o contexto
    navigate('/ai/specialized-questions', {
      state: {
        screenContext,
        company: headers?.company,
        companyName: headers?.companyName,
        user: headers?.user
      }
    })
  }

  const hasMessages = messages.length > 0

  return (
    <>
      {/* Botão Flutuante - Design Premium */}
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={handleOpenModal}
        className="fixed bottom-8 right-8 px-6 py-5 rounded-3xl shadow-2xl flex items-center justify-center gap-3 z-30 backdrop-blur-sm border-2"
        style={{
          backgroundColor: '#163965',
          borderColor: '#0f2340',
          boxShadow: '0 20px 40px rgba(22, 57, 101, 0.5), 0 0 30px rgba(22, 57, 101, 0.4)',
        }}
      >
        {/* Ícone + Texto */}
        <div className="flex items-center gap-3">
          <motion.div
            animate={{ rotate: isLoading ? 360 : 0 }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex-shrink-0"
          >
            <img
              src="/logo.png"
              alt="Lognex Logo"
              className="w-7 h-7 object-contain"
            />
          </motion.div>
          <div className="flex flex-col items-start gap-0.5">
            <span className="text-white font-bold text-base leading-tight">IA LOGNEX</span>
            <span className="text-white text-xs font-medium leading-tight opacity-90">Assistente</span>
          </div>
        </div>

        {/* Badge de mensagens */}
        {hasMessages && !isModalOpen && (
          <motion.div
            initial={{ scale: 0, rotate: -90 }}
            animate={{ scale: 1, rotate: 0 }}
            className="absolute -top-2 -right-2 bg-white text-red-600 text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center shadow-lg"
          >
            {Math.min(messages.filter((m) => m.type === 'assistant').length, 9)}
          </motion.div>
        )}

        {/* Pulse Animation quando carregando */}
        {isLoading && (
          <motion.div
            className="absolute inset-0 rounded-2xl border-2 border-white"
            initial={{ scale: 1, opacity: 1 }}
            animate={{ scale: 1.15, opacity: 0 }}
            transition={{ duration: 1.5, repeat: Infinity }}
            style={{
              borderColor: 'rgba(255, 255, 255, 0.6)',
            }}
          />
        )}

      </motion.button>


      {/* Modal */}
      <AIAssistantModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        messages={messages}
        isLoading={isLoading}
        error={error}
        onSendMessage={handleSendMessage}
        screenContext={screenContext}
        onOpenSpecializedQuestions={handleOpenSpecializedQuestions}
      />
    </>
  )
}
